import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    CreditCard,
    Calendar,
    DollarSign,
    AlertCircle,
    Plus,
    Users
} from 'lucide-react';
import { useCreateMembershipPlanMutation, CreateMembershipPlanRequest } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

const CreateMembershipPlan: React.FC = () => {
    const navigate = useNavigate();
    const [createPlan, { isLoading: isSubmitting }] = useCreateMembershipPlanMutation();

    const [formData, setFormData] = useState<CreateMembershipPlanRequest>({
        name: '',
        description: '',
        price: 0,
        duration_days: 30,
        payment_type: 'one_time',
        monthly_installment_amount: null,
        monthly_installment_count: null,
        access_level: null,
        max_bookings_per_month: 0
    });

    const [errors, setErrors] = useState<Partial<Record<keyof CreateMembershipPlanRequest, string>>>({});

    const handleInputChange = (field: keyof CreateMembershipPlanRequest, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof CreateMembershipPlanRequest, string>> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Plan name is required';
        }

        if (!formData.price || formData.price <= 0) {
            newErrors.price = 'Price is required and must be greater than 0';
        }

        if (!formData.duration_days || formData.duration_days <= 0) {
            newErrors.duration_days = 'Duration is required and must be greater than 0';
        }

        if (formData.payment_type === 'monthly_installment') {
            if (!formData.monthly_installment_amount || formData.monthly_installment_amount <= 0) {
                newErrors.monthly_installment_amount = 'Monthly installment amount is required for monthly payment plans';
            }
            if (!formData.monthly_installment_count || formData.monthly_installment_count <= 0) {
                newErrors.monthly_installment_count = 'Monthly installment count is required for monthly payment plans';
            }
        }

        if (formData.max_bookings_per_month !== undefined && formData.max_bookings_per_month < 0) {
            newErrors.max_bookings_per_month = 'Max bookings cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const payload: CreateMembershipPlanRequest = {
                name: formData.name,
                description: formData.description || null,
                price: formData.price,
                duration_days: formData.duration_days,
                payment_type: formData.payment_type,
                monthly_installment_amount: formData.payment_type === 'monthly_installment' 
                    ? formData.monthly_installment_amount || null 
                    : null,
                monthly_installment_count: formData.payment_type === 'monthly_installment' 
                    ? formData.monthly_installment_count || null 
                    : null,
                access_level: formData.access_level || null,
                max_bookings_per_month: formData.max_bookings_per_month ?? 0
            };

            await createPlan(payload).unwrap();
            toast.success('Membership plan created successfully!');
            navigate('/admin/membership-management');
        } catch (error: any) {
            console.error('Failed to create membership plan:', error);
            toast.error(error?.data?.message || 'Failed to create membership plan');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/membership-management')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Create Membership Plan</h1>
                                <p className="text-gray-600">Add a new membership plan to the system</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Basic Information
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Plan Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.name ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder="e.g., Gold Membership"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description || ''}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Describe the membership plan and its benefits..."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Price (USD) *
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.price}
                                                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.price ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        {errors.price && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.price}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Duration (Days) *
                                        </label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.duration_days}
                                                onChange={(e) => handleInputChange('duration_days', parseInt(e.target.value) || 0)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.duration_days ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="30"
                                            />
                                        </div>
                                        {errors.duration_days && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.duration_days}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Payment Type *
                                    </label>
                                    <select
                                        value={formData.payment_type}
                                        onChange={(e) => handleInputChange('payment_type', e.target.value as 'one_time' | 'monthly_installment')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="one_time">One Time Payment</option>
                                        <option value="monthly_installment">Monthly Installment</option>
                                    </select>
                                </div>

                                {formData.payment_type === 'monthly_installment' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-white border border-gray-200 rounded-lg">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Monthly Installment Amount (USD) *
                                            </label>
                                            <div className="relative">
                                                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={formData.monthly_installment_amount || ''}
                                                    onChange={(e) => handleInputChange('monthly_installment_amount', parseFloat(e.target.value) || null)}
                                                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                        errors.monthly_installment_amount ? 'border-red-300' : 'border-gray-300'
                                                    }`}
                                                    placeholder="1000"
                                                />
                                            </div>
                                            {errors.monthly_installment_amount && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <AlertCircle className="w-4 h-4 mr-1" />
                                                    {errors.monthly_installment_amount}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Number of Installments *
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.monthly_installment_count || ''}
                                                onChange={(e) => handleInputChange('monthly_installment_count', parseInt(e.target.value) || null)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.monthly_installment_count ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="12"
                                            />
                                            {errors.monthly_installment_count && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <AlertCircle className="w-4 h-4 mr-1" />
                                                    {errors.monthly_installment_count}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Access Level
                                        </label>
                                        <select
                                            value={formData.access_level || ''}
                                            onChange={(e) => handleInputChange('access_level', e.target.value || null)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="">None</option>
                                            <option value="silver">Silver</option>
                                            <option value="gold">Gold</option>
                                            <option value="platinum">Platinum</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Max Bookings Per Month
                                        </label>
                                        <div className="relative">
                                            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="number"
                                                min="0"
                                                value={formData.max_bookings_per_month}
                                                onChange={(e) => handleInputChange('max_bookings_per_month', parseInt(e.target.value) || 0)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.max_bookings_per_month ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="0 (for unlimited)"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter 0 for unlimited bookings
                                        </p>
                                        {errors.max_bookings_per_month && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.max_bookings_per_month}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex items-center justify-end space-x-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/admin/membership-management')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting || !formData.name || !formData.price || !formData.duration_days}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Create Plan
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateMembershipPlan;

